import { useEffect, useRef, useState } from "react";
import { ChevronDown, LogOut, Shield, User as UserIcon, UserCircle } from "lucide-react";
import type { AdminUser } from "../../hooks/useAdminUser";
import { ProfileModal } from "./ProfileModal";

// Menú de cuenta del admin. Reemplaza el clásico "email + badge + botón
// Salir" disperso del sidebar por un único punto de control compacto:
//
//   - Trigger: avatar circular (icon Shield para super_admin, User para
//     admin) + label del rol + chevron. Click abre dropdown.
//   - Dropdown: Perfil (modal read-only) + Logout (rojo, separador).
//   - Click fuera o Escape cierran el dropdown.
//
// Posicionado en el sidebar del admin (no arriba a la derecha — pedido
// del user para mantener el sidebar como hub único de navegación).
// El logout difiere entre dev y prod:
//   - dev: limpia el X-User-Email del localStorage y recarga.
//   - prod: window.location → /cdn-cgi/access/logout (CF Access).

type Props = {
  user: AdminUser;
  onLogoutDev: () => void; // limpia el email override en dev
};

export function UserMenu({ user, onLogoutDev }: Props) {
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Cerrar dropdown con click fuera o Escape.
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const isSuper = user.role === "super_admin";
  const RoleIcon = isSuper ? Shield : UserIcon;
  // Avatar coloreado según rol — mantener en sync con ProfileModal.
  const avatarColor = isSuper ? "bg-red-500/20 text-red-300" : "bg-blue-500/20 text-blue-300";
  const roleLabel = isSuper ? "Super admin" : "Admin";

  function handleLogout() {
    setOpen(false);
    if (import.meta.env.DEV) {
      onLogoutDev();
    } else {
      // En prod: CF Access logout endpoint limpia la sesión y redirige
      // al login. Sin '/redirect=' va al default de la app de Access.
      window.location.href = "/cdn-cgi/access/logout";
    }
  }

  return (
    <>
      <div ref={containerRef} className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center gap-2 rounded-lg bg-white/5 px-2 py-2 text-left transition hover:bg-white/10"
        >
          <div
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${avatarColor}`}
            title={user.email ?? ""}
          >
            <RoleIcon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">{roleLabel}</div>
            <div className="truncate text-[10px] opacity-50">{user.email}</div>
          </div>
          <ChevronDown
            className={`h-4 w-4 shrink-0 opacity-50 transition ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>

        {open && (
          <div
            className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-lg border border-white/10 bg-[#1a1540] shadow-2xl"
            role="menu"
          >
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setProfileOpen(true);
              }}
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-white/5"
              role="menuitem"
            >
              <UserCircle className="h-4 w-4 opacity-70" />
              Perfil
            </button>
            <div className="border-t border-white/10" />
            <button
              type="button"
              onClick={handleLogout}
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-red-300 hover:bg-red-500/10"
              role="menuitem"
            >
              <LogOut className="h-4 w-4" />
              Cerrar sesión
            </button>
          </div>
        )}
      </div>

      <ProfileModal
        open={profileOpen}
        user={user}
        onClose={() => setProfileOpen(false)}
      />
    </>
  );
}
